import React from "react";
import { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "./Drawer";
import MenuLink from "./MenuLink";
import { useAuthUser } from "../../Context/AuthUserContext";
import { useMediaQuery, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { PLAYBOOK_LOGO } from "../../theme/brandAssets";

const LOGO_URL = PLAYBOOK_LOGO;

const AppHeader = (props) => {
  const theme = useTheme();
  const { authReady, isLoggedIn, profileLoading, profile } = useAuthUser();
  const smallDevices = useMediaQuery(theme.breakpoints.down("md"));
  const brandSurface = theme.brand?.surface || theme.palette.primary.main;
  const accentOnNav = theme.palette.secondary.main;
  const [dark, setDark] = useState(true);
  const [background, setBackground] = useState({ backgroundColor: brandSurface });
  const [state, setState] = useState(false);
  const [rerender, setRerender] = useState(3);
  const login = authReady ? isLoggedIn : null;
  const navbarPending = !authReady || (isLoggedIn && profileLoading && !profile);

  const handleOpenNavMenu = () => {
    setState(false);
    setRerender(Math.round(Math.random() * 100));
    setState(true);
    setRerender(Math.round(Math.random() * 100));
  };

  useEffect(() => {
    if (dark) {
      setBackground({ backgroundColor: brandSurface });
    } else {
      setBackground({ backgroundColor: "#fff" });
    }
  }, [dark, brandSurface]);

  const logoHeight = smallDevices ? 58 : 60;

  const renderLogo = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <RouterLink to="/" style={{ display: "flex", alignItems: "center", lineHeight: 0 }}>
        <img src={LOGO_URL} alt="Playbook" style={{ height: logoHeight, display: "block", borderRadius: 4 }} />
      </RouterLink>
    </Box>
  );

  const headerIconButtonSx = {
    color: accentOnNav,
    zIndex: 2,
    transition: "background-color 0.2s ease, color 0.2s ease",
    "&:hover": { backgroundColor: "rgba(153, 6, 14, 0.14)" },
  };

  const renderDrawer = () => (
    navbarPending ? null : (
    <IconButton
      size="large"
      aria-label="open navigation menu"
      aria-controls="menu-appbar"
      aria-haspopup="true"
      onClick={handleOpenNavMenu}
      sx={{ ...headerIconButtonSx, display: { xs: "inline-flex", md: "none" } }}
    >
      <MenuIcon sx={{ fontSize: smallDevices ? "2.1rem" : "1.65rem" }} />
    </IconButton>
    )
  );

  const toolbarMobileSx = {
    position: "relative",
    boxSizing: "border-box",
    minHeight: { xs: 72, md: 64 },
    py: { xs: 1.5 },
    px: { xs: 1.25, sm: 1.75 },
    display: { xs: "flex", md: "none" },
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
    maxWidth: "100%",
  };

  const toolbarDesktopSx = {
    display: { xs: "none", md: "grid" },
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    columnGap: 2,
    minHeight: 72,
    px: { sm: 1.5, md: 2 },
    width: "100%",
    maxWidth: "100%",
  };

  const appBarContents = (
    <>
      <Toolbar disableGutters sx={toolbarMobileSx}>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            display: { xs: "flex", md: "none" },
            justifyContent: "center",
            maxWidth: { xs: "68vw", md: "320px" },
          }}
        >
          <Box sx={{ pointerEvents: "auto", lineHeight: 0 }}>{renderLogo()}</Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            minWidth: 0,
            flexShrink: 0,
            minHeight: { xs: 48, md: 48 },
            ml: "auto",
          }}
        >
          {renderDrawer()}
        </Box>
      </Toolbar>

      <Toolbar disableGutters sx={toolbarDesktopSx}>
        <Box sx={{ justifySelf: "start", minWidth: 0 }}>{renderLogo()}</Box>
        <Box sx={{ justifySelf: "center" }}>
          <MenuLink key={`${rerender}-c`} dark={dark} login={login} state={state} desktopSlot="center" />
        </Box>
        <Box sx={{ justifySelf: "end", display: "flex", alignItems: "center", minWidth: 0 }}>
          <MenuLink key={`${rerender}-p`} dark={dark} login={login} state={state} desktopSlot="profile" />
        </Box>
      </Toolbar>
    </>
  );

  const renderAppBar = () => {
    if (typeof props.type !== "undefined" && props.type === "floating") {
      let appbarStyle = { backgroundColor: "transparent", position: "absolute" };
      if (smallDevices) appbarStyle = { backgroundColor: brandSurface, position: "relative" };
      else appbarStyle = { backgroundColor: background.backgroundColor, position: "relative" };
      return (
        <AppBar position="static" sx={appbarStyle} elevation={0}>
          {appBarContents}
        </AppBar>
      );
    }

    return (
      <AppBar
        position="static"
        sx={{
          backgroundColor: background.backgroundColor,
          boxShadow: "0 1px 0 rgba(255,255,255,0.06)",
        }}
        elevation={0}
      >
        {appBarContents}
      </AppBar>
    );
  };

  return (
    <>
      {renderAppBar()}
      <Drawer login={login} dark={dark} state={state} key={rerender} />
    </>
  );
};

export default AppHeader;
